import type { StaffRole } from "@lafayette/auth";
import { Link } from "react-router";
import { activeNavigationKey } from "../navigation/active-area";
import { navIconFor } from "../navigation/nav-icons";
import type { AdminArea } from "../navigation/offering";
import { cn } from "../ui/cn";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import type { SidebarBadge } from "./shell-counts";
import { type EnvironmentFacts, type StaffPerson, UserCard } from "./UserCard";

// What is inside the sidebar: the brand, the menu with its count badges, and the user card (which holds the collapse entry, Sign out and
// the environment facts). The desktop sidebar and the drawer of a narrow screen use this same body, so the menu is one component with two
// frames.

type SidebarBodyProps = Readonly<{
  navigation: readonly AdminArea[];
  pathname: string;
  /** Icons only. The labels stay in the markup for a screen reader. */
  collapsed: boolean;
  /** A count beside an entry, by the key of the entry. Absent where there is nothing to count. */
  badges: Readonly<Partial<Record<string, SidebarBadge>>>;
  person: StaffPerson;
  roles: readonly StaffRole[];
  environment: EnvironmentFacts;
  isSigningOut: boolean;
  onSignOut: () => void;
  /** The collapse entry of the user card; absent in the drawer. */
  onToggleCollapsed?: () => void;
  /** Called after a link is followed (the drawer closes). */
  onNavigate?: () => void;
}>;

/** Concierge counts work that waits for a person and is blue; the others are quiet grey (Paper, Home). */
const BLUE_BADGES: ReadonlySet<string> = new Set(["concierge"]);

export function SidebarBody({
  navigation,
  pathname,
  collapsed,
  badges,
  person,
  roles,
  environment,
  isSigningOut,
  onSignOut,
  onToggleCollapsed,
  onNavigate,
}: SidebarBodyProps) {
  const activeKey = activeNavigationKey(navigation, pathname);
  return (
    <div className="flex h-full min-h-0 flex-col gap-7 overflow-y-auto px-2 py-3">
      <div className={cn("flex items-center gap-2.5 px-2", collapsed && "justify-center px-0")}>
        <span
          aria-hidden="true"
          className="grid size-[34px] shrink-0 place-items-center rounded-xl bg-primary font-serif text-xl font-medium text-primary-foreground"
        >
          L
        </span>
        <div className={cn("grid min-w-0 leading-none", collapsed && "sr-only")}>
          <strong className="font-serif text-[19px] leading-[22px] font-medium">Lafayette</strong>
          <span className="text-[11px] leading-[14px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
            Staff
          </span>
        </div>
      </div>

      <nav aria-label="Staff navigation">
        <ul className="m-0 grid list-none gap-0.5 p-0">
          {navigation.map((item) => {
            const active = item.key === activeKey;
            const badge = badges[item.key];
            const link = (
              <Link
                to={badge?.to ?? item.path}
                aria-current={active ? "page" : undefined}
                onClick={onNavigate}
                className={cn(
                  "relative flex min-h-11 items-center gap-3 rounded-xl px-3 text-[15px] font-medium text-sidebar-foreground no-underline transition-[color,background-color] compact:min-h-8 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:-outline-offset-2",
                  active && "bg-sidebar-accent font-semibold text-sidebar-accent-foreground",
                  collapsed && "justify-center px-0",
                )}
              >
                <Icon name={navIconFor(item.key)} size="lg" />
                <span className={cn("min-w-0 flex-1 truncate", collapsed && "sr-only")}>
                  {item.label}
                </span>
                {badge === undefined ? null : (
                  <>
                    <span
                      aria-hidden="true"
                      className={cn(
                        "rounded-full px-[9px] py-0.5 text-center text-[13px] leading-[18px] font-semibold",
                        BLUE_BADGES.has(item.key)
                          ? "bg-primary text-primary-foreground"
                          : "border border-border bg-pill text-pill-foreground",
                        collapsed && "absolute top-0.5 right-1 min-w-5 px-1 text-[11px] leading-4",
                      )}
                    >
                      {badge.text}
                    </span>
                    <span className="sr-only">
                      {badge.text} {badge.spoken}
                    </span>
                  </>
                )}
              </Link>
            );
            return (
              <li key={item.key}>
                {collapsed ? (
                  <Tooltip content={item.label} side="right">
                    {link}
                  </Tooltip>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-auto">
        <UserCard
          person={person}
          roles={roles}
          collapsed={collapsed}
          environment={environment}
          isSigningOut={isSigningOut}
          onSignOut={onSignOut}
          onToggleCollapsed={onToggleCollapsed}
        />
      </div>
    </div>
  );
}
